import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MatMomentDateModule, MomentDateAdapter } from '@angular/material-moment-adapter';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import {
    DateAdapter,
    ErrorStateMatcher,
    MAT_DATE_FORMATS,
    MAT_DATE_LOCALE,
    MatNativeDateModule,
    MatOptionModule,
} from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorIntl, MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MAT_SNACK_BAR_DEFAULT_OPTIONS, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatSortModule } from '@angular/material/sort';
import { MatStepperModule } from '@angular/material/stepper';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import { AdminPolicyDirective } from './auth/admin-policy.directive';
import { GroupManagerPolicyDirective } from './auth/group-manager-policy.directive';
import { PracticeWritePolicyDirective } from './auth/practice-write-policy.directive';
import { BannerComponent } from './banner/banner.component';
import { ChairCardComponent } from './chair-card/chair-card.component';
import { ChairOrderActionItemDialogComponent } from './chair-order-action-item-dialog/chair-order-action-item-dialog.component';
import { ChairOrderDetailsDialogComponent } from './chair-order-details-dialog/chair-order-details-dialog.component';
import { ChairOrderDetailsReportDialogComponent } from './chair-order-details-report-dialog/chair-order-details-report-dialog.component';
import { ChairOrderDetailsReportPageComponent } from './chair-order-details-report-page/chair-order-details-report-page.component';
import { ReportBlockComponent } from './chair-order-details-report-page/report-block/report-block.component';
import { ReportFieldComponent } from './chair-order-details-report-page/report-field/report-field.component';
import { ChairOrderStatusBadgeComponent } from './chair-order-status-badge/chair-order-status-badge.component';
import { ChairOrderTrackerComponent } from './chair-order-tracker/chair-order-tracker.component';
import { ConfirmationDialogComponent } from './confirmation-dialog/confirmation-dialog.component';
import { CreateUserDialogComponent } from './create-user-dialog/create-user-dialog.component';
import { DialogProgressBarComponent } from './dialog-progress-bar/dialog-progress-bar.component';
import { DisplayFieldComponent } from './display-field/display-field.component';
import { FileUploadComponent } from './file-upload/file-upload.component';
import { FlyOver } from './fly-over/fly-over';
import { FlyOverComponent } from './fly-over/fly-over.component';
import { AddErrorStateMatcher, AddFormFieldComponent } from './form-field/form-field.component';
import { GroupLookupComponent } from './group-lookup/group-lookup.component';
import { HealthScoreComponent } from './health-score/health-score.component';
import { InvoiceStatusBadgeComponent } from './invoice-status-badge/invoice-status-badge.component';
import { LabNumberViewerComponent } from './lab-number-viewer/lab-number-viewer.component';
import { LoadingSpinnerComponent } from './loading-spinner/loading-spinner.component';
import { MessageDialogComponent } from './message-dialog/message-dialog.component';
import { NewBadgeComponent } from './new-badge/new-badge.component';
import { OrderBaseSelectorComponent } from './order-base-selector/order-base-selector.component';
import { OrderDetailsDialogComponent } from './order-details-dialog/order-details-dialog.component';
import { OrderFormComponent } from './order-form/order-form.component';
import { OrderStatusBadgeComponent } from './order-status-badge/order-status-badge.component';
import { OrderTrackerComponent } from './order-tracker/order-tracker.component';
import { OrderUpdateDialogComponent } from './order-update-dialog/order-update-dialog.component';
import { PageHeaderComponent } from './page-header/page-header.component';
import { BooleanYesNoPipe } from './pipes/boolean-yes-no.pipe';
import { ChairOrderResultToColorPipe } from './pipes/chair-order-result-to-color.pipe';
import { ChairOrderStatusPipe } from './pipes/chair-order-status.pipe';
import { GenderPipe } from './pipes/gender.pipe';
import { InvoiceIntervalPipe } from './pipes/invoice-interval.pipe';
import { OrderStatusPipe } from './pipes/order-status.pipe';
import { PurgeIntervalPipe } from './pipes/purge-interval.pipe';
import { StringColorPipe } from './pipes/string-color.pipe';
import { UsageFrequenciesPipe } from './pipes/usage-frequencies.pipe';
import { WaterSuppliesPipe, WaterSuppliesShortPipe } from './pipes/water-supplies.pipe';
import { PracticeConfigurationDialogComponent } from './practice-configuration-dialog/practice-configuration-dialog.component';
import { PracticeContactDialogComponent } from './practice-contact-dialog/practice-contact-dialog.component';
import { PracticeLookupComponent } from './practice-lookup/practice-lookup.component';
import { ProfileComponent } from './profile/profile.component';
import { PurchaseOrdersDetailsDialogComponent } from './purchase-order-details-dialog/purchase-order-details-dialog.component';
import { ReportTranslationPipe } from './report/pipes/report-translation.pipe';
import { SubMenuItemComponent } from './sub-menu/sub-menu-item.component';
import { SubMenuComponent } from './sub-menu/sub-menu.component';
import { TableComponent, getPaginatorIntl } from './table/table.component';
import { TestLookupComponent } from './test-lookup/test-lookup.component';
import { TestComponent } from './test/test.component';
import { UserLookupComponent } from './user-lookup/user-lookup.component';

export const CUSTOM_DATE_FORMATS = {
    parse: {
        dateInput: 'DD/MM/YYYY',
    },
    display: {
        dateInput: 'DD/MM/YYYY',
        monthYearLabel: 'MMM YYYY',
        dateA11yLabel: 'DD/MM/YYYY',
        monthYearA11yLabel: 'MMMM YYYY',
    },
};

@NgModule({
    declarations: [
        // pipes
        PurgeIntervalPipe,
        InvoiceIntervalPipe,
        BooleanYesNoPipe,
        ChairOrderStatusPipe,
        GenderPipe,
        OrderStatusPipe,
        StringColorPipe,
        UsageFrequenciesPipe,
        WaterSuppliesPipe,
        WaterSuppliesShortPipe,
        ChairOrderResultToColorPipe,
        // components
        TableComponent,
        FileUploadComponent,
        GroupLookupComponent,
        PracticeLookupComponent,
        TestComponent,
        TestLookupComponent,
        UserLookupComponent,
        ConfirmationDialogComponent,
        AddFormFieldComponent,
        DialogProgressBarComponent,
        PracticeWritePolicyDirective,
        AdminPolicyDirective,
        GroupManagerPolicyDirective,
        SubMenuComponent,
        SubMenuItemComponent,
        PageHeaderComponent,
        MessageDialogComponent,
        ProfileComponent,
        DisplayFieldComponent,
        FlyOverComponent,
        LoadingSpinnerComponent,
        OrderBaseSelectorComponent,
        OrderStatusBadgeComponent,
        ChairOrderStatusBadgeComponent,
        OrderDetailsDialogComponent,
        ChairOrderDetailsDialogComponent,
        ChairOrderDetailsReportDialogComponent,
        ChairOrderDetailsReportPageComponent,
        OrderUpdateDialogComponent,
        InvoiceStatusBadgeComponent,
        PracticeContactDialogComponent,
        PracticeConfigurationDialogComponent,
        OrderTrackerComponent,
        ChairOrderTrackerComponent,
        OrderFormComponent,
        CreateUserDialogComponent,
        PurchaseOrdersDetailsDialogComponent,
        BannerComponent,
        ChairCardComponent,
        ChairOrderActionItemDialogComponent,
        LabNumberViewerComponent,
        ReportFieldComponent,
        ReportBlockComponent,
        ReportTranslationPipe,
        NewBadgeComponent,
        HealthScoreComponent,
    ],
    imports: [
        CommonModule,
        ReactiveFormsModule,
        RouterModule,
        // Material,
        MatAutocompleteModule,
        MatBadgeModule,
        MatButtonModule,
        MatButtonToggleModule,
        MatCheckboxModule,
        MatDatepickerModule,
        MatDialogModule,
        MatIconModule,
        MatInputModule,
        MatMenuModule,
        MatPaginatorModule,
        MatProgressBarModule,
        MatRadioModule,
        MatSelectModule,
        MatSortModule,
        MatTableModule,
        MatMomentDateModule,
        MatTooltipModule,
    ],
    providers: [
        FlyOver,
        { provide: ErrorStateMatcher, useClass: AddErrorStateMatcher },
        {
            provide: MAT_SNACK_BAR_DEFAULT_OPTIONS,
            useValue: {
                duration: 2000,
                verticalPosition: 'top',
            },
        },
        /***** DATE FORMATTING *****/
        { provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { strict: true } },
        {
            provide: DateAdapter,
            useClass: MomentDateAdapter,
            deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
        },
        { provide: MAT_DATE_FORMATS, useValue: CUSTOM_DATE_FORMATS },
        /***** CUSTOM i18n *****/
        { provide: MatPaginatorIntl, useValue: getPaginatorIntl() },
    ],
    exports: [
        CommonModule,
        ReactiveFormsModule,
        // Material
        MatAutocompleteModule,
        MatButtonModule,
        MatButtonToggleModule,
        MatCardModule,
        MatCheckboxModule,
        MatDatepickerModule,
        MatNativeDateModule,
        MatDialogModule,
        MatIconModule,
        MatInputModule,
        MatMenuModule,
        MatOptionModule,
        MatProgressBarModule,
        MatRadioModule,
        MatSelectModule,
        MatSlideToggleModule,
        MatSnackBarModule,
        MatSortModule,
        MatStepperModule,
        MatTableModule,
        MatTooltipModule,
        // Custom components
        TableComponent,
        FileUploadComponent,
        GroupLookupComponent,
        PracticeLookupComponent,
        TestComponent,
        TestLookupComponent,
        UserLookupComponent,
        AddFormFieldComponent,
        DisplayFieldComponent,
        DialogProgressBarComponent,
        PracticeWritePolicyDirective,
        AdminPolicyDirective,
        GroupManagerPolicyDirective,
        SubMenuComponent,
        SubMenuItemComponent,
        PageHeaderComponent,
        MessageDialogComponent,
        ProfileComponent,
        FlyOverComponent,
        LoadingSpinnerComponent,
        OrderBaseSelectorComponent,
        OrderStatusBadgeComponent,
        ChairOrderStatusBadgeComponent,
        OrderDetailsDialogComponent,
        ChairOrderDetailsDialogComponent,
        ChairOrderDetailsReportDialogComponent,
        ChairOrderDetailsReportPageComponent,
        OrderUpdateDialogComponent,
        InvoiceStatusBadgeComponent,
        OrderFormComponent,
        CreateUserDialogComponent,
        PurchaseOrdersDetailsDialogComponent,
        BannerComponent,
        ChairCardComponent,
        ChairOrderActionItemDialogComponent,
        LabNumberViewerComponent,
        NewBadgeComponent,
        HealthScoreComponent,
        // Custom pipes
        PurgeIntervalPipe,
        InvoiceIntervalPipe,
        BooleanYesNoPipe,
        ChairOrderStatusPipe,
        GenderPipe,
        OrderStatusPipe,
        StringColorPipe,
        UsageFrequenciesPipe,
        WaterSuppliesPipe,
        WaterSuppliesShortPipe,
        ChairOrderResultToColorPipe,
        // Report
        ReportTranslationPipe,
    ],
})
export class SharedModule {}
